import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import 'cally'

type ForecastDateRangePickerProps = {
  startDate: string
  endDate: string
  onChange: (next: { startDate: string; endDate: string }) => void
}

function formatRangeLabel(startDate: string, endDate: string): string {
  if (startDate && endDate) return `${startDate} → ${endDate}`
  if (startDate) return `${startDate} → …`
  return 'Pick date range'
}

type PopupPos = { top: number; left: number; openUp: boolean }

/**
 * DaisyUI-styled Cally `calendar-range` rendered in a fixed popup portal
 * so the surrounding map popup does not grow around the calendar.
 */
export function ForecastDateRangePicker({
  startDate,
  endDate,
  onChange,
}: ForecastDateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<PopupPos | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popupRef = useRef<HTMLDivElement | null>(null)
  const calendarHostRef = useRef<HTMLDivElement | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const popupId = useId()

  const rangeValue =
    startDate && endDate ? `${startDate}/${endDate}` : startDate ? `${startDate}/${startDate}` : ''

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const popupHeight = popupRef.current?.offsetHeight ?? 340
    const popupWidth = popupRef.current?.offsetWidth ?? 320
    const gap = 6
    const spaceBelow = window.innerHeight - rect.bottom - gap
    const openUp = spaceBelow < popupHeight && rect.top > spaceBelow
    const top = openUp ? rect.top - gap - popupHeight : rect.bottom + gap
    let left = rect.left
    left = Math.min(left, window.innerWidth - popupWidth - 8)
    left = Math.max(8, left)
    setPos({
      top: Math.max(8, Math.min(top, window.innerHeight - popupHeight - 8)),
      left,
      openUp,
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    updatePosition()
    const onWin = () => updatePosition()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onPointer = (e: MouseEvent | PointerEvent) => {
      const t = e.target as Node | null
      if (!t) return
      if (triggerRef.current?.contains(t)) return
      if (popupRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer, true)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const host = calendarHostRef.current
    if (!host) return
    const el = host.querySelector('calendar-range') as (HTMLElement & { value?: string }) | null
    if (!el) return

    if (rangeValue) el.value = rangeValue

    const handleChange = () => {
      const raw = String(el.value || '').trim()
      if (!raw) {
        onChangeRef.current({ startDate: '', endDate: '' })
        return
      }
      const [start, end] = raw.split('/')
      const startIso = (start || '').slice(0, 10)
      const endIso = (end || start || '').slice(0, 10)
      onChangeRef.current({ startDate: startIso, endDate: endIso })
    }

    el.addEventListener('change', handleChange)
    // Reposition after calendar paints
    requestAnimationFrame(() => updatePosition())
    return () => el.removeEventListener('change', handleChange)
  }, [open, rangeValue, updatePosition])

  return (
    <div className="form-control w-full">
      <div className="label py-0 pb-1">
        <span className="label-text text-[11px]">Date range</span>
      </div>
      <button
        ref={triggerRef}
        type="button"
        className="input input-bordered input-sm flex w-full items-center justify-start text-left font-normal"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {formatRangeLabel(startDate, endDate)}
      </button>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popupRef}
              id={popupId}
              role="dialog"
              aria-label="Select forecast date range"
              className="rounded-box border border-base-300 bg-base-100 p-1 shadow-xl"
              style={{
                position: 'fixed',
                top: pos?.top ?? -9999,
                left: pos?.left ?? -9999,
                zIndex: 9999,
                visibility: pos ? 'visible' : 'hidden',
              }}
            >
              <div ref={calendarHostRef}>
                {React.createElement(
                  'calendar-range',
                  {
                    class: 'cally bg-base-100 rounded-box',
                    value: rangeValue || undefined,
                  },
                  React.createElement(
                    'svg',
                    {
                      'aria-label': 'Previous',
                      className: 'fill-current size-4',
                      slot: 'previous',
                      xmlns: 'http://www.w3.org/2000/svg',
                      viewBox: '0 0 24 24',
                    },
                    React.createElement('path', { d: 'M15.75 19.5 8.25 12l7.5-7.5' }),
                  ),
                  React.createElement(
                    'svg',
                    {
                      'aria-label': 'Next',
                      className: 'fill-current size-4',
                      slot: 'next',
                      xmlns: 'http://www.w3.org/2000/svg',
                      viewBox: '0 0 24 24',
                    },
                    React.createElement('path', { d: 'm8.25 4.5 7.5 7.5-7.5 7.5' }),
                  ),
                  React.createElement('calendar-month'),
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
